import { Fragment } from 'react';
import { t } from 'i18next';
import { Avatar, Box, Button, Divider, Typography } from '@mui/material';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';

import { theme } from '@store';
import { paths } from '@common/types';

import { useCareTeam } from './hooks';
import {
  CareTeamCard,
  CareTeamCardDetails,
  CareTeamCardDetailsRow,
  CaregiverItem,
  EmergencyContactItem,
  EditCareTeam,
  EditEmergencyContactItem,
} from '@views/pages/Patient/components/CareTeam/components';
import { joinAddress } from '@util/address.ts';
import { EditCaregiverItem } from '@views/pages/Patient/components/CareTeam/components/EditCaregiverItem.tsx';

interface ICareTeamProps {
  patient: paths[`/patient/{patient_id}`]['get']['responses']['200']['content']['application/json'];
  isEditDisabled: boolean;
  tabPanelInstrumentHeight: number;
}

export const CareTeam = ({
  patient,
  isEditDisabled,
  tabPanelInstrumentHeight,
}: ICareTeamProps) => {
  const {
    practice,
    pcp,
    careNavigator,
    assessingProvider,
    geropsychProvider,
    homeHealthProvider,
    cardiologyProvider,
    palliativeCare,
    careManager,
    showPracticeDetails,
    togglePracticeDetails,
    primaryCaregiver,
    emergencyContacts,
    handleDeleteCaregiver,
    handleDeleteEmergencyContact,
    provider_type,
    internalPCPName,
    neurology,
    socialWorkerProvider,
  } = useCareTeam(patient.id);

  const isPatientInactive = patient.account_status === 'Inactive';

  return (
    <Box
      sx={{
        maxHeight: `calc(100vh - ${tabPanelInstrumentHeight + 12}px)`,
        overflowY: 'auto',
      }}
    >
      <Box
        sx={{
          mb: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          backgroundColor: '#F5F5F5',
          zIndex: 3,
        }}
      >
        <Typography variant='subtitle2'>
          {t('patient.careTeam.title')}
        </Typography>
        <EditCareTeam patient_id={patient.id} disabled={isEditDisabled} />
      </Box>
      <Box
        sx={{
          backgroundColor: theme.palette.common.white,
          padding: 2,
          border: '1px solid rgba(0, 0, 0, 0.12)',
          mb: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
          }}
        >
          {practice.partnerLogo ? (
            <Avatar
              src={practice.partnerLogo}
              sx={{ width: '48px', height: '48px' }}
              alt={practice.practiceName}
            />
          ) : (
            <Avatar
              sx={{ width: '48px', height: '48px', bgcolor: 'primary.main' }}
            >
              <MedicalServicesIcon
                aria-label='Default practice icon'
                sx={{ width: 24, height: 24 }}
              />
            </Avatar>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
            }}
          >
            <Typography variant='subtitle1'>{practice.practiceName}</Typography>
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
              {practice.partnerName}
            </Typography>
          </Box>
          <Button sx={{ height: '30px' }} onClick={togglePracticeDetails}>
            {t(
              `patient.careTeam.${showPracticeDetails ? 'showLess' : 'showMore'}`,
            )}
          </Button>
        </Box>
        {showPracticeDetails && (
          <>
            <Box sx={{ p: '16px 0' }}>
              <CareTeamCardDetails
                variant='practice'
                value1={practice.email}
                value2={practice.phone}
                value3={practice.fax}
              />
            </Box>
            <CareTeamCardDetailsRow
              icon='locationOn'
              text={practice.location}
              textWidth='80%'
            />
            <Divider
              sx={{
                height: 1,
                bgcolor: theme => theme.palette.grey[300],
                width: '100%',
                my: 2,
              }}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant='subtitle2'>
                  {t('patient.careTeam.internalPCP')}
                </Typography>
                <Typography variant='caption' color='textSecondary'>
                  {pcp.region}
                </Typography>
              </Box>
              <Typography
                variant='body2'
                sx={{
                  color: pcp.inactive ? 'text.disabled' : 'text.primary',
                }}
              >
                {`${internalPCPName} ${pcp.inactive ? '(Inactive)' : ''}`}
              </Typography>
            </Box>
          </>
        )}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gap: '8px',
          mt: '8px',
          gridTemplateColumns: 'calc(50% - 4px) calc(50% - 4px)',
          [theme.breakpoints.down('md')]: {
            gridTemplateColumns: '1fr',
          },
        }}
      >
        <CareTeamCard
          role='careNavigator'
          name={careNavigator.name}
          value1={careNavigator.email}
          value2={careNavigator.phone}
          disable={isPatientInactive}
          border
        />
        {provider_type === 'External' ? (
          <CareTeamCard
            role='externalPCP'
            name={pcp.name}
            value1={pcp.email}
            value2={pcp.phone}
            inactive={pcp.inactive}
            disable={isPatientInactive}
            border
          />
        ) : (
          <CareTeamCard
            role='careManager'
            name={careManager.name}
            value1={careManager.email}
            value2={careManager.phone_number}
            // value3={careManager.slack_link}
            disable={isPatientInactive}
            border
          />
        )}
        <CareTeamCard
          role='assessingProvider'
          name={assessingProvider.name}
          value1={assessingProvider.email}
          value2={assessingProvider.phone}
          value3={assessingProvider.slack_link}
          inactive={assessingProvider.inactive}
          disable={isPatientInactive}
          border
        />
      </Box>

      <Typography variant='subtitle2' sx={{ mb: 1, mt: 2 }}>
        {t('patient.careTeam.otherProviders')}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gap: '8px',
          gridTemplateColumns: 'calc(50% - 4px) calc(50% - 4px)',
          [theme.breakpoints.down('md')]: {
            gridTemplateColumns: '1fr',
          },
        }}
      >
        <CareTeamCard
          role='geropsych'
          name={geropsychProvider.name}
          value1={geropsychProvider.email}
          value2={geropsychProvider.phone}
          value3={geropsychProvider.slack_link}
          inactive={geropsychProvider.inactive}
          disable={isPatientInactive}
          border
        />
        <CareTeamCard
          role='cardiology'
          name={cardiologyProvider.name}
          value1={cardiologyProvider.email}
          value2={cardiologyProvider.phone}
          value3={cardiologyProvider.slack_link}
          inactive={cardiologyProvider.inactive}
          disable={isPatientInactive}
          border
        />
        <CareTeamCard
          role='palliativeCare'
          name={palliativeCare.name}
          value1={palliativeCare.email}
          value2={palliativeCare.phone}
          value3={palliativeCare.slack_link}
          inactive={palliativeCare.inactive}
          disable={isPatientInactive}
          border
        />

        <CareTeamCard
          role='homeHealthProvider'
          name={homeHealthProvider.name}
          value1={homeHealthProvider.email}
          value2={homeHealthProvider.phone}
          value3={homeHealthProvider.slack_link}
          inactive={homeHealthProvider.inactive}
          disable={isPatientInactive}
          border
        />

        <CareTeamCard
          role='neurology'
          name={neurology.name}
          value1={neurology.email}
          value2={neurology.phone}
          value3={neurology.slack_link}
          inactive={neurology.inactive}
          disable={isPatientInactive}
          border
        />

        <CareTeamCard
          role='socialWorker'
          name={socialWorkerProvider.name}
          value1={socialWorkerProvider.email}
          value2={socialWorkerProvider.phone}
          value3={socialWorkerProvider.slack_link}
          inactive={socialWorkerProvider.inactive}
          disable={isPatientInactive}
          border
        />
      </Box>

      <Typography variant='subtitle2' sx={{ mb: 1, mt: 4 }}>
        {t('patient.careTeam.subTitle')}
      </Typography>

      {primaryCaregiver && primaryCaregiver.first_name ? (
        <CareTeamCard
          role='primaryCaregiver'
          name={`${primaryCaregiver.first_name}${primaryCaregiver.middle_name ? ` ${primaryCaregiver.middle_name} ` : ' '}${primaryCaregiver.last_name}`.trim()}
          detailsVariant='contact'
          value1={primaryCaregiver.email}
          value2={primaryCaregiver.phone_number}
          location={joinAddress(primaryCaregiver)}
          cardChips={primaryCaregiver.roles}
          editAction={
            <EditCaregiverItem
              patientId={patient.id}
              disabled={isEditDisabled}
            />
          }
          removeAction={handleDeleteCaregiver}
          disableRemove={isEditDisabled}
          border
        />
      ) : (
        <Box
          sx={{
            backgroundColor: theme.palette.common.white,
            padding: 2,
            border: '1px solid rgba(0, 0, 0, 0.12)',
          }}
        >
          <CaregiverItem patientId={patient.id} disabled={isEditDisabled} />
        </Box>
      )}

      <Box
        sx={{
          mt: '8px',
          backgroundColor: theme.palette.common.white,
          padding: 2,
          border: '1px solid rgba(0, 0, 0, 0.12)',
        }}
      >
        {emergencyContacts.map((contact, idx) => {
          return (
            <Fragment key={idx}>
              <CareTeamCard
                role='emergencyContact'
                name={contact.name}
                detailsVariant='contact'
                value1={contact.email}
                value2={contact.phone_number}
                location={joinAddress(contact)}
                editAction={
                  <EditEmergencyContactItem
                    patientId={patient.id}
                    pec_id={contact.id}
                    disabled={isEditDisabled}
                  />
                }
                removeAction={() => handleDeleteEmergencyContact(contact.id)}
                disableRemove={isEditDisabled}
              />
              {idx < emergencyContacts.length - 1 && (
                <Divider
                  sx={{
                    margin: '16px 0',
                    width: '100%',
                    height: '1px',
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  }}
                />
              )}
            </Fragment>
          );
        })}
        {emergencyContacts.length > 0 && (
          <Divider
            sx={{
              margin: '16px 0',
              width: '100%',
              height: '1px',
              backgroundColor: 'rgba(0, 0, 0, 0.12)',
            }}
          />
        )}
        <EmergencyContactItem
          patientId={patient.id}
          disabled={isEditDisabled}
        />
      </Box>
    </Box>
  );
};
